import axios from 'axios'
import { toast } from '@/components/ui/sonner'
import { environment } from '@/config/environment'

interface ApiError {
  message: string
  status: number
  code?: string
  details?: any
}

interface ApiResponse<T = any> {
  data: T
  message?: string
  success: boolean
}

class ApiClient {
  private client: ReturnType<typeof axios.create>
  private retryCount = 0
  private maxRetries = 3
  private retryDelay = 1000

  constructor() {
    this.client = axios.create({
      baseURL: environment.API_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('access_token')
        if (token) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${token}`
        }
        
        // Add request ID for tracking
        config.headers = config.headers || {}
        config.headers['X-Request-ID'] = this.generateRequestId()
        
        return config
      },
      (error: any) => Promise.reject(error)
    )

    // Response interceptor
    this.client.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          localStorage.removeItem('access_token')
          localStorage.removeItem('user')
          window.location.href = '/login'
          return Promise.reject(error)
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
          toast.error('You do not have permission to perform this action')
        }

        // Handle 404 Not Found
        if (error.response?.status === 404) {
          toast.error('The requested resource was not found')
        }

        // Handle 422 Validation Error
        if (error.response?.status === 422) {
          const errors = error.response.data?.errors
          if (errors && typeof errors === 'object') {
            Object.values(errors).forEach((error: any) => {
              toast.error(Array.isArray(error) ? error[0] : error)
            })
          } else {
            toast.error('Validation failed. Please check your input.')
          }
        }

        // Handle 500+ Server Errors
        if (error.response?.status >= 500) {
          toast.error('Server error. Please try again later.')
        }

        // Handle network errors
        if (!error.response) {
          toast.error('Network error. Please check your connection.')
        }

        return Promise.reject(this.formatError(error))
      }
    )
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private formatError(error: any): ApiError {
    return {
      message: error.response?.data?.message || error.message || 'An error occurred',
      status: error.response?.status || 0,
      code: error.response?.data?.code,
      details: error.response?.data?.details,
    }
  }

  private async retryRequest<T>(requestFn: () => Promise<T>): Promise<T> {
    try {
      return await requestFn()
    } catch (error: any) {
      if (this.retryCount < this.maxRetries && this.shouldRetry(error)) {
        this.retryCount++
        await this.delay(this.retryDelay * this.retryCount)
        return this.retryRequest(requestFn)
      }
      this.retryCount = 0
      throw error
    }
  }

  private shouldRetry(error: any): boolean {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600)
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  async request<T = any>(config: any): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.request<T>(config)
      return response.data
    })
  }

  async get<T = any>(url: string, config?: any): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url })
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return this.request<T>({ ...config, method: 'POST', url, data })
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT', url, data })
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
    return this.request<T>({ ...config, method: 'PATCH', url, data })
  }

  async delete<T = any>(url: string, config?: any): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url })
  }

  // File upload helper
  async uploadFile<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent: any) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/health')
      return true
    } catch {
      return false
    }
  }
}

export const apiClient = new ApiClient()
export type { ApiError, ApiResponse } 